import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Database, Loader2, CheckCircle2, UploadCloud, Play, ShieldCheck, RefreshCw } from "lucide-react";

const StatusBadge = ({ s }) => {
  const map = {
    active: ["Attivo", "bg-emerald-100 text-emerald-800 border-emerald-200"],
    valid: ["Valido", "bg-blue-100 text-blue-800 border-blue-200"],
    imported: ["Importato", "bg-slate-100 text-slate-700 border-slate-200"],
    archived: ["Archiviato", "bg-gray-100 text-gray-600 border-gray-200"],
    VALIDATION_FAILED: ["Validazione fallita", "bg-rose-100 text-rose-800 border-rose-200"],
  };
  const [label, cls] = map[s] || [s, "bg-gray-100 text-gray-600 border-gray-200"];
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>;
};

export default function OmiDatasets() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/omi/datasets");
      setRows(r.data.datasets || []);
      setActive(r.data.active || null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel caricamento"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seedSample = async () => {
    setBusy("seed");
    try { const r = await api.post("/admin/omi/seed-sample"); toast.success(r.data.already ? "Dataset di esempio già presente." : `Dataset di esempio caricato (${r.data.activated || r.data.h2}).`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(""); }
  };

  const validate = async (id) => {
    setBusy(id + ":v");
    try { const r = await api.post(`/admin/omi/datasets/${id}/validate`); r.data.ok ? toast.success(`Valido: ${r.data.zones} zone, ${r.data.values} quotazioni.`) : toast.error(`Validazione fallita: ${(r.data.errors || [])[0] || ""}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(""); }
  };

  const activate = async (id) => {
    setBusy(id + ":a");
    try { await api.post(`/admin/omi/datasets/${id}/activate`); toast.success("Dataset attivato. Ricalcolo valutazioni avviato in background."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(""); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isZip = file.name.toLowerCase().endsWith(".zip");
    setBusy("upload");
    try {
      if (isZip) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/admin/omi/import-official", fd);
        toast.success(`Importato ${r.data.version} (${r.data.zones_count} zone, ${r.data.values_count} quotazioni). Ora validalo e attivalo.`);
      } else {
        const payload = JSON.parse(await file.text());
        const r = await api.post("/admin/omi/datasets/import", payload);
        toast.success(`Importato ${r.data.version}. Ora validalo e attivalo.`);
      }
      load();
    } catch (err) { toast.error(err?.message?.includes("JSON") ? "File JSON non valido" : (err?.response?.data?.detail || "Import non riuscito")); }
    finally { setBusy(""); e.target.value = ""; }
  };

  const uploadPerimeters = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(id + ":p");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/omi/datasets/${id}/perimeters`, fd);
      toast.success(`Perimetri caricati: ${r.data.zones_updated}/${r.data.perimeters_in_file} zone aggiornate.`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import perimetri non riuscito"); }
    finally { setBusy(""); e.target.value = ""; }
  };

  const uploadVolumes = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(id + ":vol");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/omi/datasets/${id}/volumes`, fd);
      toast.success(`Volumi importati: ${r.data.volumes_imported} comuni (anno ${r.data.year || "n.d."}).`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import volumi non riuscito"); }
    finally { setBusy(""); e.target.value = ""; }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8" data-testid="omi-datasets-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-10 w-10 rounded-lg bg-[#007BFF] flex items-center justify-center"><Database className="h-5 w-5 text-white" /></div>
        <div>
          <h1 className="font-head text-2xl font-extrabold tracking-tight">Dataset immobiliari OMI</h1>
          <p className="text-sm text-muted-foreground">Amministrazione · Sistema · Quotazioni Osservatorio Mercato Immobiliare</p>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-border bg-card p-4" data-testid="omi-active-card">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Dataset attivo</div>
        {active ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-head text-xl font-extrabold">{active.version}</span>
            <StatusBadge s="active" />
            <span className="text-xs text-muted-foreground">{active.zones_count} zone · {active.values_count} quotazioni</span>
            <span className="text-xs text-muted-foreground">Attivato: {(active.activated_at || "").slice(0, 10)}</span>
          </div>
        ) : <div className="text-sm text-muted-foreground">Nessun dataset attivo. Precarica quello di esempio o importane uno.</div>}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button data-testid="omi-seed-sample-btn" onClick={seedSample} disabled={!!busy} className="bg-[#007BFF] hover:bg-[#0069D9] text-white">
          {busy === "seed" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}Precarica dataset di esempio (Verona)
        </Button>
        <label className="inline-flex items-center gap-2 cursor-pointer text-sm font-medium px-4 py-2 rounded-md border border-border hover:bg-muted transition-colors" data-testid="omi-upload-label">
          {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}Importa dataset ufficiale (.zip)
          <input type="file" accept=".zip,application/zip,application/json,.json" className="hidden" onChange={uploadFile} data-testid="omi-upload-input" />
        </label>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="omi-refresh-btn"><RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />Aggiorna</Button>
      </div>

      <p className="text-[11px] text-muted-foreground mt-2 max-w-2xl">
        Scarica dall'area riservata dell'Agenzia delle Entrate ("Forniture dati OMI", accesso Entratel/Fisconline)
        lo <strong>ZIP delle quotazioni</strong> (file *_ZONE.csv + *_VALORI.csv) e caricalo qui: viene importato,
        validato e attivato senza conversioni manuali. Per la precisione massima carica anche i <strong>perimetri
        di zona (KML)</strong> sul dataset (pulsante "Perimetri"): senza perimetri la stima usa i valori medi del
        comune (affidabilità ridotta). Lo storico dei semestri precedenti non viene sovrascritto.
      </p>

      <div className="mt-5" data-testid="omi-datasets-list">
        <h2 className="font-semibold text-sm mb-2">Cronologia dataset</h2>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-6"><Loader2 className="h-4 w-4 animate-spin" />Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6">Nessun dataset importato.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((d) => (
              <div key={d.id} data-testid={`omi-row-${d.version}`} className="flex items-center gap-3 flex-wrap bg-card border border-border rounded-lg p-3">
                <span className="font-semibold">{d.version}</span>
                <StatusBadge s={d.status} />
                <span className="text-xs text-muted-foreground">{d.zones_count} zone · {d.values_count} quot.</span>
                <span className="text-xs text-muted-foreground">{d.source}</span>
                <div className="ml-auto flex gap-2">
                  <label className="inline-flex items-center gap-1 cursor-pointer text-xs font-medium px-2.5 py-1.5 rounded-md border border-border hover:bg-muted transition-colors" data-testid={`omi-perimeters-${d.version}`}>
                    {busy === d.id + ":p" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}Perimetri (KML)
                    <input type="file" accept=".kml,.xml" className="hidden" onChange={(e) => uploadPerimeters(d.id, e)} />
                  </label>
                  <label className="inline-flex items-center gap-1 cursor-pointer text-xs font-medium px-2.5 py-1.5 rounded-md border border-border hover:bg-muted transition-colors" data-testid={`omi-volumes-${d.version}`}>
                    {busy === d.id + ":vol" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}Volumi (ZIP)
                    <input type="file" accept=".zip" className="hidden" onChange={(e) => uploadVolumes(d.id, e)} />
                  </label>
                  {d.status !== "active" && (
                    <Button size="sm" variant="outline" data-testid={`omi-validate-${d.version}`} onClick={() => validate(d.id)} disabled={busy === d.id + ":v"}>
                      {busy === d.id + ":v" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}Valida
                    </Button>
                  )}
                  {d.status === "valid" && (
                    <Button size="sm" data-testid={`omi-activate-${d.version}`} onClick={() => activate(d.id)} disabled={busy === d.id + ":a"} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      {busy === d.id + ":a" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}Attiva
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
